import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'

import { getProfileStorageSettings, saveProfileStorageSettings } from '../../api/profileStorage'
import { useToast } from '../../context/ToastContext'
import { showApiErrorToast } from '../../lib/apiErrorToast'
import { ThemeReveal } from '../motion/ThemeMotion'

type Props = {
  angular: boolean
  canSave: boolean
}

export function ProfileStorageSettingsSection({ angular, canSave }: Props) {
  const { showToast } = useToast()
  const [cloudBucket, setCloudBucket] = useState('')
  const [cloudAk, setCloudAk] = useState('')
  const [cloudSk, setCloudSk] = useState('')
  const [cloudCdn, setCloudCdn] = useState('')
  const [cloudSubNote, setCloudSubNote] = useState('')
  const [cloudPrefix, setCloudPrefix] = useState('library')
  const [cloudAkHint, setCloudAkHint] = useState('')
  const [cloudConfigured, setCloudConfigured] = useState(false)
  const [cloudSaving, setCloudSaving] = useState(false)
  const [allowedModuleKeys, setAllowedModuleKeys] = useState<string[]>([])
  const [enabledModuleKeys, setEnabledModuleKeys] = useState<string[]>(['general'])

  useEffect(() => {
    const loadCloud = async () => {
      try {
        const s = await getProfileStorageSettings()
        setCloudConfigured(s.configured)
        setCloudBucket(s.bucketName)
        setCloudCdn(s.cdnRootUrl)
        setCloudSubNote(s.subdomainNote)
        setCloudPrefix(s.libraryRootPrefix || 'library')
        setCloudAkHint(s.accessKeyMasked)
        const allowed = s.allowedModuleKeys?.length ? s.allowedModuleKeys : ['general']
        const selected = (s.enabledModuleKeys?.length ? s.enabledModuleKeys : ['general']).filter((key) =>
          allowed.includes(key)
        )
        setAllowedModuleKeys(allowed)
        setEnabledModuleKeys(selected.length ? selected : [allowed[0]])
        setCloudAk('')
        setCloudSk('')
      } catch (e) {
        showApiErrorToast(showToast, e, '云端存储配置加载失败', 'warning')
      }
    }
    void loadCloud()
    const onChange = () => void loadCloud()
    window.addEventListener('glyphicai-settings-changed', onChange)
    return () => window.removeEventListener('glyphicai-settings-changed', onChange)
  }, [showToast])

  const onSaveCloud = async (e: FormEvent) => {
    e.preventDefault()
    if (cloudSaving || !canSave) return
    setCloudSaving(true)
    try {
      const next = await saveProfileStorageSettings({
        bucketName: cloudBucket.trim(),
        accessKey: cloudAk,
        secretKey: cloudSk,
        cdnRootUrl: cloudCdn.trim(),
        subdomainNote: cloudSubNote.trim(),
        libraryRootPrefix: cloudPrefix.trim() || 'library',
        enabledModuleKeys,
      })
      setCloudConfigured(next.configured)
      setCloudAkHint(next.accessKeyMasked)
      setAllowedModuleKeys(next.allowedModuleKeys ?? [])
      setEnabledModuleKeys(next.enabledModuleKeys?.length ? next.enabledModuleKeys : ['general'])
      setCloudAk('')
      setCloudSk('')
      window.dispatchEvent(new Event('glyphicai-settings-changed'))
      showToast({ message: '已保存', variant: 'success' })
    } catch (err) {
      showApiErrorToast(showToast, err, '保存失败', 'error')
    } finally {
      setCloudSaving(false)
    }
  }

  const shellRound = angular ? 'rounded-none' : 'rounded-[var(--radius)]'
  const inputClass = [
    'mt-1.5 w-full border border-line bg-surface px-3 py-2.5 text-sm outline-none ring-accent focus:ring-2',
    angular ? 'rounded-none' : 'rounded-[var(--radius-sm)]',
  ].join(' ')
  const moduleLabels: Record<string, string> = {
    timeline: '时间线',
    reference: '参考图',
    comic: '漫画正文',
    cover: '漫画封面',
    general: '图库',
    temporary: '临时中转',
  }

  const toggleModule = (moduleKey: string) => {
    setEnabledModuleKeys((prev) => {
      if (prev.includes(moduleKey)) {
        const next = prev.filter((key) => key !== moduleKey)
        return next.length ? next : prev
      }
      return [...prev, moduleKey]
    })
  }

  return (
    <ThemeReveal delay={0.08}>
      <section
        className={['border border-line bg-panel p-5 shadow-card sm:p-6 lg:p-4', shellRound].join(' ')}
      >
        <h2 className="text-sm font-semibold text-ink">私有云存储（七牛）</h2>
        <p className="mt-1 text-xs leading-relaxed text-ink-soft">
          绑定自有图库后，创作与图库上传将优先写入你的存储空间；未配置时使用平台存储。
        </p>
        <form onSubmit={onSaveCloud} className="mt-5 space-y-5">
          {cloudConfigured && cloudAkHint ? (
            <p className="text-xs text-ink-soft">
              密钥示意：<span className="font-mono text-ink">{cloudAkHint}</span>（留空表示不修改）
            </p>
          ) : null}
          <div>
            <label htmlFor="profile-cloud-bucket" className="text-xs font-medium text-ink-soft">
              空间名称
            </label>
            <input
              id="profile-cloud-bucket"
              value={cloudBucket}
              onChange={(e) => setCloudBucket(e.target.value)}
              autoComplete="off"
              disabled={!canSave}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="profile-cloud-ak" className="text-xs font-medium text-ink-soft">
              AccessKey
            </label>
            <input
              id="profile-cloud-ak"
              type="password"
              value={cloudAk}
              onChange={(e) => setCloudAk(e.target.value)}
              placeholder={cloudConfigured ? '不修改请留空' : '必填'}
              autoComplete="off"
              disabled={!canSave}
              className={`${inputClass} font-mono`}
            />
          </div>
          <div>
            <label htmlFor="profile-cloud-sk" className="text-xs font-medium text-ink-soft">
              SecretKey
            </label>
            <input
              id="profile-cloud-sk"
              type="password"
              value={cloudSk}
              onChange={(e) => setCloudSk(e.target.value)}
              placeholder={cloudConfigured ? '不修改请留空' : '必填'}
              autoComplete="off"
              disabled={!canSave}
              className={`${inputClass} font-mono`}
            />
          </div>
          <div>
            <label htmlFor="profile-cloud-cdn" className="text-xs font-medium text-ink-soft">
              外链根地址
            </label>
            <input
              id="profile-cloud-cdn"
              value={cloudCdn}
              onChange={(e) => setCloudCdn(e.target.value)}
              placeholder="https://"
              autoComplete="off"
              disabled={!canSave}
              className={`${inputClass} font-mono`}
            />
          </div>
          <div>
            <label htmlFor="profile-cloud-sub" className="text-xs font-medium text-ink-soft">
              备注
            </label>
            <input
              id="profile-cloud-sub"
              value={cloudSubNote}
              onChange={(e) => setCloudSubNote(e.target.value)}
              autoComplete="off"
              disabled={!canSave}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="profile-cloud-prefix" className="text-xs font-medium text-ink-soft">
              对象键根前缀
            </label>
            <input
              id="profile-cloud-prefix"
              value={cloudPrefix}
              onChange={(e) => setCloudPrefix(e.target.value)}
              placeholder="library"
              autoComplete="off"
              disabled={!canSave}
              className={`${inputClass} font-mono`}
            />
          </div>
          {allowedModuleKeys.length ? (
            <div>
              <p className="text-xs font-medium text-ink-soft">作用模块</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {allowedModuleKeys.map((moduleKey) => (
                  <label
                    key={moduleKey}
                    className="inline-flex items-center gap-2 rounded-full border border-line px-3 py-1.5 text-xs text-ink-soft"
                  >
                    <input
                      type="checkbox"
                      checked={enabledModuleKeys.includes(moduleKey)}
                      onChange={() => toggleModule(moduleKey)}
                      disabled={!canSave}
                    />
                    {moduleLabels[moduleKey] || moduleKey}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-ink-soft">
                未选择或配置失效时，系统会回落到平台对应模块的存储实例。
              </p>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
            <button
              type="submit"
              disabled={cloudSaving || !canSave}
              className="rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-60"
            >
              {cloudSaving ? '保存中…' : '保存'}
            </button>
            {!canSave ? (
              <p className="text-xs text-ink-soft">
                保存自有图库配置需要「图库-自有存储」权限，请联系管理员开通。
              </p>
            ) : null}
          </div>
        </form>
      </section>
    </ThemeReveal>
  )
}
